import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Skeleton,
  LinearProgress,
  Chip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { useAppSettings } from '../context/SettingsContext';
import { dayCount, fetchIncomeReport, type IncomeReport } from '../services/incomeReporting';

const PERIODS = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

const CHANNEL_COLORS: Record<string, string> = {
  stripe: '#a78bfa',
  xero: '#34d399',
  meta: '#60a5fa',
};

const IncomeReportPage: React.FC = () => {
  const { settings } = useAppSettings();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<IncomeReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const to = format(new Date(), 'yyyy-MM-dd');
        const from = format(subDays(new Date(), days - 1), 'yyyy-MM-dd');
        const r = await fetchIncomeReport(settings, { from, to });
        if (!cancelled) {
          setReport(r);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings, days]);

  const fmt = (n: number, cur = report?.currency || 'AUD') =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);

  const channelIcons: Record<string, React.ReactNode> = {
    stripe: <CreditCardOutlinedIcon />,
    xero: <AccountBalanceOutlinedIcon />,
    meta: <FacebookOutlinedIcon />,
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0ea5e9 120%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <TrendingUpIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={800}>
            Income by channel
          </Typography>
        </Stack>
        <Typography sx={{ opacity: 0.9, maxWidth: 520 }}>
          Revenue from Stripe and Xero in one view. Meta shows connected social accounts (shop revenue coming soon).
        </Typography>
        <ToggleButtonGroup
          value={days}
          exclusive
          onChange={(_, v) => v && setDays(v)}
          size="small"
          sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiToggleButton-root': { color: '#fff', borderColor: 'rgba(255,255,255,0.2)' } }}
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.days} value={p.days}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(145deg, #fef3c7, #fff)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                Total income ({report ? dayCount(report.from, report.to) : days}d)
              </Typography>
              {loading ? (
                <Skeleton height={48} width={160} />
              ) : (
                <Typography variant="h3" fontWeight={800} color="primary.dark">
                  {fmt(report?.totalPaid ?? report?.total ?? 0)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {(report?.channels || []).map((ch) => (
          <Grid item xs={12} sm={6} md={4} key={ch.channel}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {ch.label}
                    </Typography>
                    {loading ? (
                      <Skeleton height={40} width={120} sx={{ mt: 1 }} />
                    ) : (
                      <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                        {ch.channel === 'meta' && ch.amount === 0
                          ? `${ch.count} account${ch.count === 1 ? '' : 's'}`
                          : fmt(ch.amount, ch.currency)}
                      </Typography>
                    )}
                    {!loading && ch.count > 0 && ch.channel !== 'meta' && (
                      <Typography variant="caption" color="text.secondary">
                        {ch.count} transactions
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ color: CHANNEL_COLORS[ch.channel] || '#94a3b8' }}>{channelIcons[ch.channel]}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Daily revenue
              </Typography>
              <Box sx={{ width: '100%', height: 320 }}>
                {loading ? (
                  <Skeleton variant="rectangular" height={320} />
                ) : (
                  <ResponsiveContainer>
                    <AreaChart data={report?.series || []}>
                      <defs>
                        <linearGradient id="stripeGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmt(Number(v ?? 0))} />
                      <Legend />
                      <Area type="monotone" dataKey="stripe" name="Stripe" stroke="#a78bfa" fill="url(#stripeGrad)" />
                      <Area type="monotone" dataKey="xero" name="Xero" stroke="#34d399" fill="#34d39922" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Channel mix
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                {loading ? (
                  <Skeleton variant="rectangular" height={280} />
                ) : (
                  <ResponsiveContainer>
                    <BarChart
                      data={(report?.channels || []).filter((c) => c.amount > 0 || c.channel === 'stripe')}
                      layout="vertical"
                      margin={{ left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmt(Number(v ?? 0))} />
                      <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                        {(report?.channels || [])
                          .filter((c) => c.amount > 0 || c.channel === 'stripe')
                          .map((c) => (
                            <Cell key={c.channel} fill={CHANNEL_COLORS[c.channel] || '#94a3b8'} />
                          ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                {(report?.channels || []).map((c) => (
                  <Chip
                    key={c.channel}
                    size="small"
                    label={c.label}
                    sx={{ bgcolor: `${CHANNEL_COLORS[c.channel]}22`, color: CHANNEL_COLORS[c.channel] }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IncomeReportPage;
